/*  ============================================================================
    TeamReporting.jsx — internal per-tech 1:1 performance view
    Nav: Reporting → Team Reporting   ·   tab id: "teamreporting"

    ⚠ INTERNAL ONLY. Never client-facing, never on a share link, never on a
      monthly report. This is a coaching tool for one-on-ones, not a leaderboard.

    Data contract: see TEAM_REPORTING_BRIEF.md (project doc) — one call,
    GET /api/team-reporting/:resourceId?start=&end=  returns the whole payload.
    Every section degrades on its own: a missing source renders an "unavailable"
    placeholder, never a misleading 0.
    ============================================================================ */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import apiClient from "../api/apiClient";

/* ⚠ EVERY CALL GOES THROUGH apiClient, AND THAT IS A BUG FIX, NOT A TIDY-UP.
   This component used raw fetch() with `Bearer ${window.__mspToken}`. That
   global is populated by the token-refresh loop inside MspOsApp, and it is set
   ASYNCHRONOUSLY — `onGetAuthToken()` is an MSAL acquireTokenSilent promise
   whose .then() assigns it. The technician fetch runs in a useEffect with []
   deps, so it fires ONCE at mount; if the token had not landed yet it sent an
   empty Bearer, took a 401, and never retried. The dropdown then read
   "Could not load techs" until you navigated away and back.

   It is not theoretical and it is not intermittent in the useful sense: a hard
   refresh while already sitting on this tab reproduces it, because the child's
   mount effect runs before the parent's token has resolved.

   apiClient acquires a token PER REQUEST through MSAL, refreshing it silently,
   so there is no global to race. It is what every other tab already uses —
   MonthlyReports.jsx does exactly this — and REACT_APP_BACKEND_URL is set in
   the SWA workflow, so the base URL resolves in production. A retry on 401
   would have papered over the symptom; this removes the class. */

/** Axios rejects on non-2xx; surface the status the way the UI copy expects. */
const errMsg = (e) => (e?.response?.status ? `HTTP ${e.response.status}` : (e?.message || "request failed"));

/* ── date helpers ─────────────────────────────────────────────────────────── */
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
/* ⚠ THE REPORTING WEEK RUNS SUNDAY → SATURDAY. This is the WEEK BOUNDARY and
   NOT the working week: a Sun–Sat period still contains five Mon–Fri business
   days and still expects 38 hours. The backend's BUSINESS_HOURS (Mon–Fri
   08:30–17:00) is deliberately untouched — it is shared with the client-facing
   monthly report, so widening it would silently move every response-time figure
   in the app, including numbers already sent to clients.

   ⚠ NO ISO WEEK NUMBERS. ISO weeks are Monday-based, so they are a day out
   against this boundary and, near a year end, a whole week out. Periods are
   keyed and labelled by the explicit Sunday date.

   The backend has the same helper (teamReportingService.weekStartOf). Separate
   bundles cannot share a module, so the contract is one definition per repo,
   used everywhere in it — if one moves, move the other in the same change. */
const sundayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); return x; };

const PERIODS = [
  { id: "prevweek",  label: "Previous week" },
  { id: "thisweek",  label: "This week" },
  { id: "last4",     label: "Last 4 weeks" },
  { id: "prevmonth", label: "Previous month" },
];

function periodRange(id, now = new Date()) {
  const thisSun = sundayOf(now);
  if (id === "thisweek")  return { start: ymd(thisSun), end: ymd(addDays(thisSun, 6)) };
  if (id === "last4")     return { start: ymd(addDays(thisSun, -28)), end: ymd(addDays(thisSun, -1)) };
  if (id === "prevmonth") {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last  = new Date(now.getFullYear(), now.getMonth(), 0);
    return { start: ymd(first), end: ymd(last) };
  }
  // prevweek (default) — the last COMPLETE Sunday-to-Saturday week
  const prevSun = addDays(thisSun, -7);
  return { start: ymd(prevSun), end: ymd(addDays(prevSun, 6)) };
}

const fmtDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
};
const fmtRange = (start, end) =>
  `${new Date(`${start}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short" })} – ${new Date(`${end}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}`;
const num = (v, dp = 1) => (v === null || v === undefined || Number.isNaN(v) ? "—" : Number(v).toFixed(dp));

/* ── RAG colouring ────────────────────────────────────────────────────────────
   `lowerIsBetter` flips the comparison (response time: under target = good).   */
const GREEN = "#27ae60", AMBER = "#e67e22", RED = "#e74c3c", GREY = "var(--text3)";
function rag(value, target, lowerIsBetter = false) {
  if (value === null || value === undefined || !target) return GREY;
  const ratio = value / target;
  if (lowerIsBetter) return ratio <= 1 ? GREEN : ratio <= 1.25 ? AMBER : RED;
  return ratio >= 1 ? GREEN : ratio >= 0.8 ? AMBER : RED;
}
/* Days-since-last-correspondence colouring, for the open-ticket list.

   NOT `rag()`. That function scores a value against a TARGET, and there is no
   target for how long a ticket may sit - the number is a prompt for a
   conversation, not a pass mark. Anything under a working week is plain: a
   ticket worked yesterday is normal, and painting it green would claim an
   approval nobody gave. */
const daysColour = (d) => (d == null ? GREY : d >= 10 ? RED : d >= 5 ? AMBER : "var(--text2)");

/* Date AND time, because "3 days" and "Tuesday afternoon" answer different
   questions in a 1:1 and the second is the one that jogs a memory. */
const fmtWhen = (iso) => {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "\u2014";
  return d.toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

/* The printed handout caps the open-ticket list; see the card for why. */
const PRINT_TICKET_CAP = 15;

const scoreColour = (s) => (s >= 8 ? GREEN : s >= 6 ? AMBER : RED);
const scoreEmoji  = (s) => (s === "Positive" ? "😊" : s === "Negative" ? "😟" : "😐");

/* ── shared chrome ────────────────────────────────────────────────────────── */
const Card = ({ title, kicker, right, children, accent = "var(--strat)", section }) => (
  <div data-section={section} className="tr-card" style={{ background: "white", border: "1px solid var(--border)", borderTop: `3px solid ${accent}`, borderRadius: 8, overflow: "hidden", marginBottom: 14 }}>
    <div style={{ padding: "10px 14px 9px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text)", letterSpacing: "0.01em" }}>{title}</div>
        {kicker && <div style={{ fontSize: 9, color: "var(--text3)", marginTop: 2 }}>{kicker}</div>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

const Empty = ({ children }) => (
  <div style={{ padding: "18px 14px", textAlign: "center", fontSize: 10.5, color: "var(--text3)", lineHeight: 1.5 }}>{children}</div>
);

const Kpi = ({ label, value, unit, target, teamAvg, colour, sub }) => (
  <div style={{ flex: "1 1 180px", minWidth: 170, background: "white", border: "1px solid var(--border)", borderLeft: `4px solid ${colour}`, borderRadius: 8, padding: "11px 13px" }}>
    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text3)", marginBottom: 6 }}>{label}</div>
    <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
      <span style={{ fontSize: 26, fontWeight: 800, color: colour, lineHeight: 1 }}>{value}</span>
      {unit && <span style={{ fontSize: 10, color: "var(--text3)", fontWeight: 600 }}>{unit}</span>}
    </div>
    <div style={{ fontSize: 9, color: "var(--text3)", marginTop: 6, lineHeight: 1.5 }}>
      {target !== null && target !== undefined && <div>Target <strong style={{ color: "var(--text2)" }}>{target}</strong></div>}
      {teamAvg !== null && teamAvg !== undefined && <div>Team avg <strong style={{ color: "var(--text2)" }}>{teamAvg}</strong></div>}
      {sub && <div>{sub}</div>}
    </div>
  </div>
);

/* Horizontal bar used in the per-day strip. Width is a % of `max`. */
const Bar = ({ value, max, colour, height = 8 }) => (
  <div style={{ background: "var(--s2)", borderRadius: 4, height, width: "100%", overflow: "hidden" }}>
    <div style={{ width: `${Math.max(0, Math.min(100, max ? (value / max) * 100 : 0))}%`, height: "100%", background: colour, borderRadius: 4, transition: "width .2s" }} />
  </div>
);

/* Ticket evidence row — used by the response drill-down and the comms-gap list */
const TicketRow = ({ t, rightLabel, rightColour, children }) => (
  <div style={{ display: "flex", gap: 10, padding: "8px 14px", borderTop: "1px solid var(--border)", alignItems: "flex-start" }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        <span style={{ color: "var(--strat)", fontWeight: 700 }}>#{t.ticketNumber}</span> {t.title}
      </div>
      <div style={{ fontSize: 9, color: "var(--text3)", marginTop: 2 }}>{t.companyName}{t.queue ? ` · ${t.queue}` : ""}{t.createDate ? ` · opened ${new Date(t.createDate).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}</div>
      {children}
    </div>
    {rightLabel && <div style={{ flexShrink: 0, fontSize: 12, fontWeight: 800, color: rightColour || "var(--text2)", textAlign: "right" }}>{rightLabel}</div>}
  </div>
);

/* Expand/collapse list — declared at module level so the hook is legal
   (the app's ESLint config rejects hooks inside map/IIFE callbacks). */
/* ⚠ IN PRINT MODE THIS RENDERS EVERY ITEM, AND THAT IS THE WHOLE POINT.
   On screen it shows 5 of N behind a "show more". Printed collapsed, it
   silently truncates the evidence — the named slow tickets, the note excerpts,
   the recent IT Glue entries — which is the entire reason for taking paper into
   a 1:1. A sheet that looks complete and is missing the drill-down is worse
   than no sheet. */
const Expandable = ({ items, initial = 5, render, moreLabel = "more", printing = false }) => {
  const [open, setOpen] = useState(false);
  /* ⚠ DEFENCE IN DEPTH, ADDED AFTER A WHITE SCREEN. `items.slice` on an
     undefined prop throws inside render, and a render exception here unmounts
     the ENTIRE page - not this list, not this card, the whole tab. One
     malformed payload should cost the reader one empty section, so a
     non-array degrades to nothing rendered. The callers still guard on the
     array; this is the layer that stops the next mistake being a blank page. */
  const list = Array.isArray(items) ? items : [];
  const shown = (open || printing) ? list : list.slice(0, initial);
  return (
    <>
      {shown.map(render)}
      {!printing && list.length > initial && (
        <div onClick={() => setOpen((o) => !o)}
          style={{ padding: "7px 14px", borderTop: "1px solid var(--border)", fontSize: 10, color: "var(--strat)", cursor: "pointer", fontWeight: 600, background: "var(--bg)" }}>
          {open ? "▲ Show less" : `▼ Show ${list.length - initial} ${moreLabel}`}
        </div>
      )}
    </>
  );
};

/* Per-client sentiment card — its own component for the same hook reason */
const SentimentClientCard = ({ c }) => {
  const [open, setOpen] = useState(false);
  const col = scoreColour(c.score);
  const hasWhy = (c.reasons?.length || 0) + (c.evidence?.length || 0) > 0;
  return (
    <div style={{ borderTop: "1px solid var(--border)" }}>
      <div onClick={() => hasWhy && setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "flex-start", gap: 9, padding: "9px 14px", cursor: hasWhy ? "pointer" : "default" }}>
        <div style={{ width: 4, borderRadius: 2, alignSelf: "stretch", background: col, flexShrink: 0, minHeight: 26 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
            <span style={{ fontSize: 11 }}>{scoreEmoji(c.sentiment)}</span>
            <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--text)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.companyName}</span>
            {hasWhy && <span style={{ fontSize: 8, background: "rgba(11,128,165,0.1)", color: "var(--strat)", padding: "1px 5px", borderRadius: 3, fontWeight: 700, flexShrink: 0 }}>{open ? "why ▾" : "why ▸"}</span>}
            <span style={{ fontSize: 12, fontWeight: 800, color: col, flexShrink: 0 }}>{c.score}<span style={{ fontSize: 8, fontWeight: 400, color: "var(--text3)" }}>/10</span></span>
          </div>
          <div style={{ fontSize: 9.5, color: "var(--text3)", lineHeight: 1.4 }}>{c.summary}</div>
          <div style={{ fontSize: 9, color: "var(--text3)", marginTop: 2 }}>{c.noteCount} notes · {c.ticketCount} ticket{c.ticketCount !== 1 ? "s" : ""}</div>
        </div>
      </div>
      {open && (
        <div style={{ background: "var(--bg)", borderTop: "1px solid var(--border)", padding: "10px 14px 12px" }}>
          {c.reasons?.length > 0 && (
            <>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text3)", marginBottom: 5 }}>Why this score</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: c.evidence?.length ? 10 : 0 }}>
                {c.reasons.map((r, i) => (
                  <div key={i} style={{ fontSize: 10, color: "var(--text2)", lineHeight: 1.45, padding: "6px 9px", background: "white", border: "1px solid var(--border)", borderRadius: 5 }}>{r}</div>
                ))}
              </div>
            </>
          )}
          {c.evidence?.length > 0 && (
            <>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text3)", marginBottom: 5 }}>From the tickets</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {c.evidence.map((e, i) => (
                  <div key={i} style={{ fontSize: 10, color: "var(--text2)", lineHeight: 1.45, padding: "6px 9px", background: "white", borderLeft: "3px solid var(--border2)", borderRadius: "0 5px 5px 0", fontStyle: "italic" }}>
                    “{e.quote}”
                    <span style={{ fontStyle: "normal", color: "var(--text3)", fontSize: 9 }}>
                      {" "}— #{e.ticketNumber}{e.at ? `, ${new Date(e.at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const CoachingBlock = ({ heading, icon, colour, bg, items, numbered }) => {
  if (!items?.length) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: colour, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 13 }}>{icon}</span> {heading}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {items.map((x, i) => (
          <div key={i} style={{ padding: "7px 10px", background: bg, border: `1px solid ${colour}22`, borderRadius: 6, fontSize: 10.5, color: "var(--text2)", lineHeight: 1.45, display: "flex", gap: 7 }}>
            {numbered && <span style={{ color: colour, fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>}
            <span>{x}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── main component ───────────────────────────────────────────────────────── */
/* ── PRINT MODE ───────────────────────────────────────────────────────────────
   Entered by URL, not by prop, so the sheet is a bookmarkable address per
   technician and MspOs.js only has to render this component bare:

     /?print=team-reporting&resourceId=29682927&start=2026-08-23&end=2026-08-29

   ⛔ AUTH IS UNCHANGED — MSAL, same origin, same session. There is NO share
   token and NO public route, and there must never be one: this sheet names a
   person and scores them, so a forwardable URL is the one thing it must not
   have. The token never appears in the address; apiClient acquires one per
   request, which is also why this path no longer depends on MspOsApp's
   token-refresh loop having run.

   ⚠ The period comes from the URL and is NOT recomputed here. A print tab
   opened on a Sunday must render the same week the page was showing, not
   silently re-derive "previous week" against the print tab's own clock. */
function readPrintIntent() {
  if (typeof window === "undefined") return null;
  const q = new URLSearchParams(window.location.search);
  if (q.get("print") !== "team-reporting") return null;
  const start = q.get("start"), end = q.get("end");
  const ymdRe = /^\d{4}-\d{2}-\d{2}$/;
  return {
    resourceId: q.get("resourceId") || null,
    start: ymdRe.test(start || "") ? start : null,
    end: ymdRe.test(end || "") ? end : null,
  };
}

export default function TeamReporting() {
  const printIntent = useMemo(readPrintIntent, []);
  const [printingInPlace, setPrintingInPlace] = useState(false);
  /* One flag for both routes to paper: the bookmarkable print URL, and the
     in-place button. Both expand every list, hide the chrome and drop the build
     affordances — the only difference is that one arrives in a fresh tab. */
  const printing = !!printIntent || printingInPlace;
  const [techs, setTechs] = useState([]);
  const [techsError, setTechsError] = useState(null);
  const [resourceId, setResourceId] = useState(printIntent?.resourceId ?? null);
  const [periodId, setPeriodId] = useState("prevweek");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [sentimentError, setSentimentError] = useState(null);
  const [sentimentProgress, setSentimentProgress] = useState(null);   // { done, total }
  const pollRef = useRef(null);
  const [reportBusy, setReportBusy] = useState(null);   // "html" | "pdf" | "tech" | null
  const [reportError, setReportError] = useState(null);
  const [openTickets, setOpenTickets] = useState(null);
  const [openTicketsError, setOpenTicketsError] = useState(null);

  /* The printed period comes from the URL and is NOT recomputed. A tab opened
     on a Sunday must render the week the page was showing, not re-derive
     "previous week" against its own clock and print a different report. */
  const range = useMemo(
    () => (printIntent && printIntent.start && printIntent.end
      ? { start: printIntent.start, end: printIntent.end }
      : periodRange(periodId)),
    [periodId, printIntent]);

  /* techs list — Autotask resources on the service desk */
  useEffect(() => {
    let cancelled = false;
    apiClient.get("/api/team-reporting/techs")
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.techs || [];
        setTechs(list);
        setResourceId((cur) => cur ?? (list[0]?.resourceId ?? null));
      })
      .catch((e) => { if (!cancelled) setTechsError(errMsg(e)); });
    return () => { cancelled = true; };
  }, []);

  /* CURRENT OPEN TICKETS - AS AT NOW, NOT PERIOD-SCOPED
     THE DEPENDENCY LIST IS THE POINT: `resourceId` and nothing else. Every
     other fetch on this page re-runs when the period changes; an open ticket is
     open today whatever week is on screen, so re-fetching it against a period
     would be at best wasted and at worst read as "these were their open tickets
     that week" - which is not a question this data can answer. The card carries
     its own "as at" stamp for the same reason.

     This call never carries a rebuild flag: the backend's 15-minute cache is
     deliberate, and this is the most expensive query the feature added. (The
     source-level test that counts rebuild flags on this page would see the
     literal in this comment, which is why it is not written here.) */
  useEffect(() => {
    if (!resourceId) return undefined;
    let cancelled = false;
    setOpenTickets(null);
    setOpenTicketsError(null);
    apiClient.get(`/api/team-reporting/${resourceId}/open-tickets`, { timeout: 60000 })
      // ⚠ UNWRAP THE ENVELOPE. Every route on this router answers
      // { <name>: payload } - { techs }, { roster }, { config }, { sentiment } -
      // and this one answers { openTickets }. Storing the envelope instead of
      // the payload left `.count` and `.items` undefined, which sailed past the
      // `count === 0` guard below (undefined === 0 is false) and handed
      // Expandable an undefined array. That threw inside render and WHITE-
      // SCREENED THE WHOLE TAB. Read the payload out by name, like the
      // sentiment poll beneath does.
      .then(({ data: j }) => { if (!cancelled) setOpenTickets(j?.openTickets || null); })
      .catch((e) => { if (!cancelled) setOpenTicketsError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [resourceId]);

  /* The team comparison line is its OWN endpoint, not part of the per-tech
     payload — it is built from shared queries so it costs about one tech's
     worth of Autotask calls instead of N. Fetched alongside and merged in;
     a failure here leaves the "Team avg" captions blank and nothing else,
     because a missing comparison must never block a technician's own numbers. */
  const load = useCallback((force) => {
    if (!resourceId) return;
    if (force) setRefreshing(true); else setLoading(true);
    setError(null);
    const period = `start=${range.start}&end=${range.end}`;
    // 60s, not apiClient's 30s default: a cold per-tech build is several
    // Autotask queries plus the day-chunked IT Glue pull, and measured ~15-20s.
    Promise.all([
      apiClient.get(`/api/team-reporting/${resourceId}?${period}${force ? "&refresh=1" : ""}`, { timeout: 60000 })
        .then((r) => r.data),
      apiClient.get(`/api/team-reporting/team?${period}`, { timeout: 60000 })
        .then((r) => r.data)
        .catch(() => null),
    ])
      .then(([payload, teamResp]) => {
        setData({ ...payload, teamAverage: teamResp?.teamAverage || payload.teamAverage || null });
      })
      .catch((e) => { setError(errMsg(e)); setData(null); })
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [resourceId, range.start, range.end]);

  useEffect(() => { load(false); }, [load]);

  /* ── FIRE THE PRINT DIALOG ONCE THE SHEET IS ACTUALLY COMPLETE ─────────────
     Printing mid-load produces a page of spinners, and nobody notices until it
     is on paper. So this waits for the payload — which already carries the
     merged team average and whatever sentiment is CACHED — and then hands one
     frame to layout before opening the dialog.

     ⚠ It deliberately does NOT call the sentiment endpoint. That endpoint kicks
     a background build (one AI call per client), so a print tab would both
     spend money and wait on something that may take minutes. Print renders the
     cached analysis, and the sentiment card says plainly when there is none —
     an empty card on a 1:1 sheet is worse than a stated absence. */
  const printedRef = useRef(false);
  useEffect(() => {
    // Gated on the URL intent, NOT on `printing` — the in-place button opens
    // the dialog itself, and both firing would give two print dialogs.
    if (!printIntent || printedRef.current) return;
    if (loading || !data) return;
    printedRef.current = true;
    const id = setTimeout(() => window.print(), 300);
    return () => clearTimeout(id);
  }, [printIntent, loading, data]);

  /* ── The printable report ───────────────────────────────────────────────────
     ⛔ The report route is AUTHENTICATED, and there is no public link to open.
     That is the whole security posture of this document: it names staff and
     scores them, so unlike the client monthly report it has no shareable URL
     at all.

     A plain `window.open` of the API URL could not work even if we wanted it
     to — a new tab sends no Authorization header. So the HTML is fetched WITH
     the bearer token and written into a blank tab. The tab therefore has no
     address anyone could copy, forward or bookmark; nothing is minted server
     side, and there is no token to leak or revoke. Ctrl-P prints it normally.

     The blank tab is opened SYNCHRONOUSLY inside the click handler, before the
     await. Open it afterwards and the browser's popup blocker treats it as
     unsolicited and silently swallows it. */
  const openReport = useCallback((onlyThisTech) => {
    const win = window.open("", "_blank");
    if (!win) {
      setReportError("Your browser blocked the new tab. Allow pop-ups for this site and try again.");
      return;
    }
    win.document.write(`<!doctype html><meta charset="utf-8"><title>Building report…</title>
      <div style="font-family:system-ui;padding:40px;color:#52514e">Building the report for this period…</div>`);
    setReportBusy(onlyThisTech ? "tech" : "html");
    setReportError(null);
    const qs = `start=${range.start}&end=${range.end}${onlyThisTech && resourceId ? `&resourceId=${resourceId}` : ""}`;
    // 120s: a cold report is one full per-tech build EACH, run sequentially
    // because Autotask 429s on a burst. Measured 37s for four technicians.
    apiClient.get(`/api/team-reporting/report?${qs}`, { responseType: "text", timeout: 120000 })
      .then(({ data: html }) => {
        win.document.open();
        win.document.write(html);
        win.document.close();
      })
      .catch((e) => {
        win.close();
        setReportError(`Could not build the report: ${errMsg(e)}`);
      })
      .finally(() => setReportBusy(null));
  }, [range.start, range.end, resourceId]);

  const downloadReportPdf = useCallback(() => {
    setReportBusy("pdf");
    setReportError(null);
    apiClient.get(`/api/team-reporting/report/pdf?start=${range.start}&end=${range.end}`,
      { responseType: "blob", timeout: 120000 })
      .then(({ data: blob }) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `team-report-${range.start}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1500);
      })
      .catch((e) => setReportError(`Could not build the PDF: ${errMsg(e)}`))
      .finally(() => setReportBusy(null));
  }, [range.start, range.end]);

  /* Sentiment is DELIBERATELY not part of the page load. It is the only
     expensive half — an AI call per client this tech touched — so it is cached
     separately and run on request. `GET /:resourceId` returns whatever is
     already cached for the period; this button is what builds one. Rebuilding
     the metrics must never re-spend the AI call, which is why "Rebuild from
     source" above does not trigger it. */
  /* The build runs in the BACKGROUND on the server — one Anthropic call per
     client the tech touched, which for a busy week is a dozen and cannot fit
     inside Azure's hard 230-second request ceiling. So the endpoint answers
     immediately with whatever is analysed so far plus `partial`, and this
     polls until it settles.

     Partial results are rendered as they land rather than held behind a
     spinner: the first clients are readable while the rest build, which is the
     same thing the Autotask dashboard's sentiment build does. */
  const analyseSentiment = useCallback(() => {
    if (!resourceId) return;
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    setAnalysing(true);
    setSentimentError(null);
    setSentimentProgress(null);

    const url = `/api/team-reporting/${resourceId}/sentiment?start=${range.start}&end=${range.end}`;
    let attempts = 0;

    const tick = () => {
      apiClient.get(url)
        .then(({ data: j }) => {
          const s = j?.sentiment;
          if (!s) {
            setSentimentError("No customer-facing notes to analyse for this technician and period.");
            setAnalysing(false);
            return;
          }
          // Show what has landed so far, every poll.
          setData((cur) => (cur ? { ...cur, sentiment: s } : cur));
          setSentimentProgress({ done: s.done ?? 0, total: s.total ?? 0 });

          if (!s.partial) { setAnalysing(false); setSentimentProgress(null); return; }

          // ~4 minutes of polling at 4s. A build that has not finished by then
          // has something wrong with it, and silently polling forever would
          // hide that rather than surface it.
          if (++attempts > 60) {
            setSentimentError("Still building after several minutes — reopen the card later to pick up where it got to.");
            setAnalysing(false);
            return;
          }
          pollRef.current = setTimeout(tick, 4000);
        })
        .catch((e) => { setSentimentError(errMsg(e)); setAnalysing(false); });
    };
    tick();
  }, [resourceId, range.start, range.end]);

  // Stop polling when the tech or period changes, or the tab unmounts — a poll
  // writing into `data` for a technician who is no longer on screen would put
  // one person's sentiment under another's name.
  useEffect(() => () => {
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
  }, [resourceId, range.start, range.end]);

  const tech = techs.find((t) => String(t.resourceId) === String(resourceId));
  const targets = data?.targets || {};
  const resp = data?.responsiveness;
  const thr  = data?.throughput;
  const ts   = data?.timesheet;
  const sent = data?.sentiment;
  const glue = data?.itGlue;
  const gaps = data?.commsGaps;
  const team = data?.teamAverage || {};

  const maxDayHours = Math.max(targets.timesheetHoursPerDay || 7.6, ...(ts?.perDay || []).map((d) => d.hours || 0));

  /* ── PRINT CSS ────────────────────────────────────────────────────────────
     ⚠ print-color-adjust IS NOT OPTIONAL HERE. Browsers drop background fills
     when printing, and this sheet is ENTIRELY RAG-coloured — the KPI borders,
     the day bars, the confidence pills. Printed without it every metric comes
     out white, and the reader assumes the colours were never there rather than
     that they were stripped. Both the -webkit- prefix and the standard property
     are set, on the elements AND on their coloured children.

     break-inside: avoid keeps a card, an evidence row or a coaching block from
     splitting across a page; nothing is more confusing on paper than a quote
     whose attribution is on the next sheet.

     The running footer repeats the internal marking on EVERY page, because a
     printed sheet of one person's performance left on a desk has no other way
     of saying what it is. */
  const printCss = `
    @page { size: A4 portrait; margin: 12mm 12mm 16mm; }
    @media print {
      html, body { background: #fff !important; }
      .tr-print-hide { display: none !important; }
      .tr-card, .tr-row, .tr-block {
        break-inside: avoid; page-break-inside: avoid;
        -webkit-print-color-adjust: exact; print-color-adjust: exact;
      }
      .tr-card * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .tr-foot { position: fixed; bottom: 0; left: 0; right: 0; font-size: 8px;
                 color: #666; border-top: 1px solid #ddd; padding-top: 3px; }
      a[href]:after { content: ""; }
    }
    @media screen { .tr-foot { display: none; } }
  `;

  return (
    <div className="scroll" style={{ flex: 1, padding: printing ? "0" : "16px 20px 40px", background: printing ? "#fff" : "var(--bg)" }}>
      {printing && <style>{printCss}</style>}
      {printing && (
        <div className="tr-foot">
          {tech ? tech.name : ""} · {fmtRange(range.start, range.end)} ·
          INTERNAL — coaching use only, not for distribution ·
          generated {new Date().toLocaleString("en-AU")}
        </div>
      )}
      {printing && (
        <div className="tr-block" style={{ border: "1px solid #f2c6c2", background: "#fdecea", borderRadius: 8, padding: "9px 13px", marginBottom: 14, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".5px", textTransform: "uppercase", color: "#a32a24" }}>
            Internal — Datatech · not for client distribution
          </div>
          <div style={{ fontSize: 10, color: "var(--text2)", marginTop: 2 }}>
            Named staff performance data, for a coaching conversation. Every figure is shown with its
            workload context and none of them are combined into a single score.
          </div>
        </div>
      )}

      {/* ── page header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontFamily: "var(--ff-head)", fontWeight: 700, fontSize: 18, color: "var(--text)", marginBottom: 2 }}>Team Reporting</div>
          <div style={{ fontSize: 10.5, color: "var(--text3)" }}>
            Pre-1:1 performance view · {fmtRange(range.start, range.end)}
            {data?.period?.businessDays ? ` · ${data.period.businessDays} business day${data.period.businessDays !== 1 ? "s" : ""}` : ""}
          </div>
        </div>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--danger)", background: "var(--danger-d)", border: "1px solid rgba(231,76,60,0.25)", padding: "3px 8px", borderRadius: 4, alignSelf: "center" }}>
          🔒 Internal only — never client-facing
        </span>
      </div>

      {/* ── controls ── */}
      <div className="tr-print-hide" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <select className="inp" style={{ width: 220, marginBottom: 0 }} value={resourceId || ""}
          onChange={(e) => setResourceId(e.target.value || null)}>
          {techs.length === 0 && <option value="">{techsError ? "Could not load techs" : "Loading technicians…"}</option>}
          {techs.map((t) => <option key={t.resourceId} value={t.resourceId}>{t.name}</option>)}
        </select>
        <div style={{ display: "flex", gap: 4 }}>
          {PERIODS.map((p) => (
            <button key={p.id} className={`btn ${periodId === p.id ? "btn-S" : ""}`} style={{ fontSize: 10, padding: "6px 11px" }}
              onClick={() => setPeriodId(p.id)}>{p.label}</button>
          ))}
        </div>
        <button className="btn" style={{ fontSize: 10, padding: "6px 11px", marginLeft: "auto" }}
          onClick={() => load(true)} disabled={loading || refreshing || !resourceId}>
          {refreshing ? "↻ Rebuilding…" : "↻ Rebuild from source"}
        </button>
      </div>

      {/* Printable report. Internal only — the page it opens says so on every
          copy, because a printed sheet of named staff metrics has no other way
          of telling you what it is. */}
      <div className="tr-print-hide" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <button className="btn" style={{ fontSize: 10, padding: "6px 11px" }}
          onClick={() => openReport(false)} disabled={!!reportBusy || loading}>
          {reportBusy === "html" ? "Building…" : "🖨 Open report"}
        </button>
        {/* The 1:1 handout is the PAGE, printed — not a server-side
            re-implementation of it. Opening it as a URL means every section and
            every drill-down comes along by construction, and the two can never
            drift the way the roster PDF did. Bookmarkable per technician. */}
        <button className="btn" style={{ fontSize: 10, padding: "6px 11px" }}
          onClick={() => window.open(
            `${window.location.origin}/?print=team-reporting&resourceId=${resourceId}&start=${range.start}&end=${range.end}`,
            "_blank")}
          disabled={loading || !resourceId}>
          {`📄 1:1 handout${tech ? ` — ${tech.name.split(" ")[0]}` : ""}`}
        </button>
        <button className="btn" style={{ fontSize: 10, padding: "6px 11px" }}
          onClick={downloadReportPdf} disabled={!!reportBusy || loading}>
          {reportBusy === "pdf" ? "Rendering…" : "⬇ Download PDF"}
        </button>
        {/* ⚡ THE FASTEST PATH TO PAPER, AND IT CANNOT DRIFT FROM THE PAGE
            BECAUSE IT *IS* THE PAGE. Zero fetches by definition — it prints the
            DOM already on screen, with the same @media print rules and every
            collapsed list expanded. The new-tab URL still exists because it is
            bookmarkable per technician and survives a reload; this is for when
            you are already looking at the right thing. */}
        <button className="btn" style={{ fontSize: 10, padding: "6px 11px" }}
          onClick={() => { setPrintingInPlace(true); setTimeout(() => { window.print(); setPrintingInPlace(false); }, 120); }}
          disabled={loading || !data}>
          🖨 Print this page
        </button>
        <span style={{ fontSize: 9.5, color: "var(--text3)" }}>
          Internal only — never send to a client. Opens in a new tab; print with Ctrl-P.
        </span>
      </div>

      {reportError && (
        <div style={{ padding: "8px 14px", background: "var(--danger-d)", border: "1px solid rgba(231,76,60,0.25)", borderRadius: 8, fontSize: 10.5, color: "var(--danger)", marginBottom: 12 }}>
          {reportError}
        </div>
      )}

      {error && (
        <div style={{ padding: "10px 14px", background: "var(--danger-d)", border: "1px solid rgba(231,76,60,0.25)", borderRadius: 8, fontSize: 11, color: "var(--danger)", marginBottom: 14 }}>
          Could not load this period: {error}
        </div>
      )}
      {loading && !data && <Empty>Building {tech ? tech.name : "technician"}’s metrics for this period…</Empty>}

      {/* ⚠ ZERO BUSINESS DAYS IS LIVE, NOT THEORETICAL. The week runs Sunday to
          Saturday, so on a Sunday "This week" holds no Mon-Fri day yet. Every
          per-day figure already comes back null rather than NaN, but rendering
          a strip of dashes and zeros reads exactly like a technician who did
          nothing — which is the worst thing to show before a 1:1. Say what is
          true instead. */}
      {data && data.period && data.period.notStarted && (
        <div style={{ padding: "16px 18px", background: "white", border: "1px solid var(--border)", borderRadius: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>This week has not started yet</div>
          <div style={{ fontSize: 10.5, color: "var(--text3)", lineHeight: 1.5 }}>
            The reporting week runs Sunday to Saturday, and {fmtRange(range.start, range.end)} has no
            working days in it so far — so there is nothing to measure. Pick <strong>Previous week</strong>
            for the last complete week.
          </div>
        </div>
      )}

      {data && !(data.period && data.period.notStarted) && (
        <>
          {/* ── KPI strip ── */}
          <div data-section="kpis" className="tr-card" style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <Kpi label="First response" value={num(resp?.avgHours, 2)} unit="h avg"
              colour={rag(resp?.avgHours, targets.firstResponseHours, true)}
              target={targets.firstResponseHours ? `${targets.firstResponseHours}h` : null}
              teamAvg={team.firstResponseHours != null ? `${num(team.firstResponseHours, 2)}h` : null}
              sub={resp ? `median ${num(resp.medianHours, 2)}h · p90 ${num(resp.p90Hours, 2)}h · ${resp.firstResponseCount} ticket${resp.firstResponseCount !== 1 ? "s" : ""}` : null} />
            <Kpi label="Tickets closed / day" value={num(thr?.closedPerDay, 1)} unit="/ day"
              colour={rag(thr?.closedPerDay, targets.ticketsClosedPerDay)}
              target={targets.ticketsClosedPerDay ? `${targets.ticketsClosedPerDay}/day` : null}
              teamAvg={team.ticketsClosedPerDay != null ? `${num(team.ticketsClosedPerDay, 1)}/day` : null}
              sub={thr ? `${thr.ticketsClosed} closed · ${thr.ticketsTouched} touched` : null} />
            <Kpi label="Timesheet" value={num(ts?.totalHours ? ts.totalHours / (data.period?.businessDays || 1) : null, 1)} unit="h / day"
              colour={rag(ts?.totalHours ? ts.totalHours / (data.period?.businessDays || 1) : null, targets.timesheetHoursPerDay)}
              target={`${targets.timesheetHoursPerDay || 7.6}h/day`}
              teamAvg={team.timesheetHoursPerDay != null ? `${num(team.timesheetHoursPerDay, 1)}h/day` : null}
              sub={ts ? `${num(ts.totalHours, 1)}h of ${num(ts.expectedHours, 1)}h · ${num(ts.utilisationPct, 0)}% captured` : null} />
            <Kpi label="IT Glue activity" value={glue?.matched === false ? "—" : String((glue?.created || 0) + (glue?.updated || 0))} unit="touches"
              colour={glue?.matched === false ? GREY : rag((glue?.created || 0) + (glue?.updated || 0), targets.itGlueTouchesPerWeek)}
              target={targets.itGlueTouchesPerWeek ? `${targets.itGlueTouchesPerWeek}/wk` : null}
              teamAvg={team.itGlueTouches != null ? String(team.itGlueTouches) : null}
              sub={glue?.matched === false ? "no IT Glue identity mapped" : glue ? `${glue.created} created · ${glue.updated} updated` : null} />
          </div>

          {/* ── per-day strip ── */}
          <Card section="day-by-day" title="Day by day" kicker={`Hours logged against a ${targets.timesheetHoursPerDay || 7.6}h day (8:30–17:00) · tickets closed · notes written`} accent="var(--tact)">
            {!ts?.perDay?.length ? <Empty>No day-level data for this period.</Empty> : (
              <div style={{ padding: "4px 0" }}>
                <div style={{ display: "flex", padding: "5px 14px", fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text3)", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ width: 110 }}>Day</div>
                  <div style={{ flex: 1 }}>Hours logged</div>
                  <div style={{ width: 68, textAlign: "right" }}>Hours</div>
                  <div style={{ width: 60, textAlign: "right" }}>Closed</div>
                  <div style={{ width: 60, textAlign: "right" }}>Notes</div>
                </div>
                {ts.perDay.map((d) => {
                  const day = thr?.perDay?.find((x) => x.date === d.date) || {};
                  const col = !d.isWorkingDay ? GREY : rag(d.hours, d.expected || targets.timesheetHoursPerDay || 7.6);
                  return (
                    <div key={d.date} style={{ display: "flex", alignItems: "center", padding: "6px 14px", borderBottom: "1px solid var(--border)", opacity: d.isWorkingDay ? 1 : 0.45 }}>
                      {/* A dimmed row would otherwise read as a weekend. Leave is
                          the reason this day expects less, so it has to say so —
                          the whole point of tracking it is that nobody has to
                          guess why a day looks empty. */}
                      <div style={{ width: 110, fontSize: 10, color: "var(--text2)", fontWeight: 500 }}>
                        {fmtDay(d.date)}
                        {d.leaveHours > 0 && (
                          <span style={{ marginLeft: 5, fontSize: 8, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--cal)" }}>
                            leave{d.isWorkingDay ? ` ${num(d.leaveHours, 1)}h` : ""}
                          </span>
                        )}
                      </div>
                      <div style={{ flex: 1, paddingRight: 14 }}><Bar value={d.hours || 0} max={maxDayHours} colour={col} /></div>
                      <div style={{ width: 68, textAlign: "right", fontSize: 11, fontWeight: 700, color: col }}>
                        {num(d.hours, 1)}<span style={{ fontSize: 8, color: "var(--text3)", fontWeight: 400 }}>/{num(d.expected ?? targets.timesheetHoursPerDay ?? 7.6, 1)}</span>
                      </div>
                      <div style={{ width: 60, textAlign: "right", fontSize: 11, color: "var(--text2)", fontWeight: 600 }}>{day.ticketsClosed ?? "—"}</div>
                      <div style={{ width: 60, textAlign: "right", fontSize: 11, color: "var(--text2)", fontWeight: 600 }}>{day.notes ?? "—"}</div>
                    </div>
                  );
                })}
                {ts.daysUnder > 0 && (
                  <div style={{ padding: "8px 14px", fontSize: 10, color: "var(--warn)", background: "var(--warn-d)" }}>
                    {/* Compare against the DAY's expectation, not the flat target:
                        the backend already reduces `expected` for a part-leave day,
                        so a tech who logged their full remaining 4h on a half-leave
                        day must not be listed here as having fallen short. */}
                    ⚠ {ts.daysUnder} working day{ts.daysUnder !== 1 ? "s" : ""} under target.
                    {" "}Worth asking what was happening on {ts.perDay.filter((d) => d.isWorkingDay && d.expected > 0 && (d.hours || 0) < d.expected).slice(0, 3).map((d) => fmtDay(d.date)).join(", ")} before reading anything into it.
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* CURRENT OPEN TICKETS
              First, because it is the only section that is about NOW. Everything
              below is a retrospective on a chosen week; this is what is on their
              plate as you sit down, and it is the natural thing to open with. */}
          <Card section="open-tickets" title="Current open tickets"
            kicker="As at now — NOT the selected period. Open tickets assigned to this technician in L1/L2/L3, longest since the customer last heard from us first."
            accent={openTickets?.count ? "var(--strat)" : "var(--ops)"}
            right={openTickets && (
              <span style={{ fontSize: 10, color: "var(--text3)", textAlign: "right", lineHeight: 1.4 }}>
                <strong style={{ fontSize: 12, color: "var(--text)" }}>{openTickets.count}</strong> open<br />
                <span style={{ fontSize: 8.5 }}>as at {fmtWhen(openTickets.asAt)}</span>
              </span>
            )}>
            {openTicketsError ? <Empty>Could not load open tickets — {openTicketsError}</Empty>
              : !openTickets ? <Empty>Loading current open tickets…</Empty>
              /* Guard on the ARRAY, not on `count`. A count-only guard reads
                 as equivalent and is not: an absent count is `undefined`, and
                 `undefined === 0` is false, so a malformed payload walks
                 straight past it into the list. */
              : !openTickets.items?.length ? <Empty>No open tickets assigned to this technician in L1/L2/L3.</Empty>
              : (
                <>
                  {/* PRINTED, THIS LIST IS CAPPED AND SAYS SO. On screen every
                      ticket is reachable; on paper sixty rows would bury the
                      rest of the sheet, so the 15 longest-silent print and the
                      count of what is not shown prints with them - a silently
                      short list would read as a complete one. */}
                  <Expandable printing={printing} items={printing ? openTickets.items.slice(0, PRINT_TICKET_CAP) : openTickets.items}
                    initial={8} moreLabel="tickets"
                    render={(t) => {
                      const silent = t.businessDaysSince;
                      /* No correspondence at all: the ticket's own age is the
                         only clock there is. A ticket opened this morning is not
                         a failure, and colouring it like a three-week silence is
                         how a red row stops meaning anything. */
                      const col = daysColour(silent == null ? t.businessDaysSinceCreated : silent);
                      const days = silent == null ? t.businessDaysSinceCreated : silent;
                      return (
                        <TicketRow key={t.ticketId} t={t}
                          rightLabel={days == null ? "—" : `${num(days, 1)}d`}
                          rightColour={col}>
                          <div style={{ fontSize: 9, color: "var(--text3)", marginTop: 3 }}>
                            <span style={{ fontWeight: 700, color: "var(--text2)" }}>{t.statusLabel}</span>
                            {" · "}{num(t.hoursByTech, 2)}h logged by {tech?.name?.split(" ")[0] || "them"}
                            {/* Shown only when it differs: 12h on the ticket
                                against 1h of theirs is a hand-off, not a measure
                                of how long they have spent on it. */}
                            {t.hoursTotal != null && (
                              <span> · <span style={{ color: "var(--cal)", fontWeight: 600 }}>{num(t.hoursTotal, 2)}h on the ticket in total</span></span>
                            )}
                          </div>
                          {/* "Last correspondence" is what actually REACHED the
                              customer, not the last note - most notes are
                              internal, and a ticket can look freshly worked
                              while the customer has not had a word in a
                              fortnight. The DIRECTION is carried because the two
                              are opposite conversations: they owe us a reply, or
                              we owe them one. */}
                          <div style={{ fontSize: 9.5, marginTop: 3, color: col, fontWeight: 600 }}>
                            {silent == null
                              ? <>No customer correspondence on this ticket at all
                                {t.businessDaysSinceCreated != null && (
                                  <span style={{ fontWeight: 400, color: "var(--text3)" }}> — opened {num(t.businessDaysSinceCreated, 1)} business days ago</span>
                                )}</>
                              : `${t.lastCorrespondenceDirection === "customer" ? "↙ Customer wrote in" : "↗ We wrote to the customer"} ${fmtWhen(t.lastCorrespondenceAt)}`}
                          </div>
                          {/* MISSING-ASSET MARKER - a hygiene note, not a score.
                              NO RAG COLOUR AND NO COUNT, deliberately: plenty of
                              tickets legitimately have no asset (email, accounts,
                              licences, a site-wide network fault), so a red row
                              or a percentage would invent a failing grade out of
                              tickets that are entirely correct. Naming the asset
                              is what turns a nag into a one-click fix.
                              It carries into the printed handout unhidden - the
                              handout IS this page, and a marker that vanished on
                              paper would be missing from the one place the
                              conversation actually happens. */}
                          {t.missingAsset && (
                            <div style={{ fontSize: 9, color: "var(--text3)", marginTop: 3 }}>
                              🏷 No asset attached — looks like{" "}
                              <strong style={{ color: "var(--text2)" }}>{t.missingAsset.name}</strong>
                            </div>
                          )}
                          {t.description && (
                            <div style={{ fontSize: 9.5, color: "var(--text2)", marginTop: 4, padding: "6px 9px", background: "var(--bg)", borderLeft: "3px solid var(--border)", borderRadius: "0 5px 5px 0", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
                              {t.description}
                            </div>
                          )}
                        </TicketRow>
                      );
                    }} />
                  {printing && openTickets.count > PRINT_TICKET_CAP && (
                    <div style={{ padding: "7px 14px", borderTop: "1px solid var(--border)", fontSize: 9.5, color: "var(--text3)", background: "var(--bg)" }}>
                      Showing the {PRINT_TICKET_CAP} longest-silent of {openTickets.count} open tickets.
                    </div>
                  )}
                </>
              )}
          </Card>

          {/* ── responsiveness drill-down ── */}
          <Card section="responsiveness" title="Responsiveness — first response to the customer"
            kicker="Business hours (Mon–Fri 08:30–17:00 Perth). Derived from the first client-facing note or time entry — NOT Autotask's firstResponseDateTime."
            accent="var(--strat)"
            right={resp?.slaMetPct != null && (
              <span style={{ fontSize: 10, fontWeight: 700, color: rag(resp.slaMetPct, 90), background: "var(--s2)", padding: "3px 8px", borderRadius: 4 }}>
                {num(resp.slaMetPct, 0)}% within target
              </span>
            )}>
            {!resp?.tickets?.length ? <Empty>No first responses attributed to this technician in the period.</Empty> : (
              <Expandable printing={printing} items={[...resp.tickets].sort((a, b) => (b.hours || 0) - (a.hours || 0))} initial={5} moreLabel="tickets"
                render={(t) => (
                  <TicketRow key={t.ticketId} t={t}
                    rightLabel={`${num(t.hours, 2)}h`}
                    rightColour={rag(t.hours, targets.firstResponseHours, true)}>
                    <div style={{ fontSize: 9, color: "var(--text3)", marginTop: 2 }}>
                      first touch via {t.via === "note" ? "ticket note" : "time entry"}
                      {t.firstResponseAt ? ` at ${new Date(t.firstResponseAt).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                    </div>
                  </TicketRow>
                )} />
            )}
          </Card>

          {/* ── comms gap ── */}
          <Card section="comms-gaps" title="⚠ Replied to the customer, but no customer email selected"
            kicker="Notes that read as customer communication where Autotask sent no notification to an external address — the customer never received it."
            accent={gaps?.count ? "var(--danger)" : "var(--ops)"}
            right={gaps && (
              <span style={{ fontSize: 11, fontWeight: 800, color: gaps.count ? "var(--danger)" : "var(--ops)" }}>{gaps.count}</span>
            )}>
            {!gaps ? <Empty>Notification history unavailable for this period.</Empty>
              /* Not "none happened" — "none detected". Notes opening with a
                 colleague's first name are deliberately skipped to avoid
                 flagging internal messages, so a reply to a customer who shares
                 a first name with someone on the team is skipped too. Claiming
                 a clean sweep here would be a false assurance in a tool that is
                 meant to start a conversation, not close one. */
              : !gaps.items?.length ? <Empty>✅ No missed customer emails detected — every note matched to an external recipient.<br /><span style={{ fontSize: 9 }}>Notes addressed to a colleague’s first name are skipped, so this is not a guarantee of none.</span></Empty>
              : (
                <Expandable printing={printing} items={gaps.items} initial={4} moreLabel="notes"
                  render={(g) => (
                    <TicketRow key={g.noteId} t={g}
                      rightLabel={g.confidence === "high" ? "likely" : "possible"}
                      rightColour={g.confidence === "high" ? RED : AMBER}>
                      <div style={{ fontSize: 9.5, color: "var(--text2)", marginTop: 4, padding: "6px 9px", background: "var(--bg)", borderLeft: "3px solid var(--danger)", borderRadius: "0 5px 5px 0", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
                        {g.excerpt}
                      </div>
                      <div style={{ fontSize: 9, color: "var(--text3)", marginTop: 3 }}>
                        note {new Date(g.createdAt).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        {/* publish is displayed only if the backend labelled it. It carries NO
                            delivery signal — a genuine customer reply and a purely internal note
                            on the same ticket both come back publish: 1 (verified against the live
                            tenant 2026-08). Never derive a label from the integer here, and never
                            let this line imply the customer did or didn't receive it — that is
                            what `notifiedTo` is for. */}
                        {g.publishLabel ? ` · ${g.publishLabel}` : ""}
                        {" · "}{g.notifiedTo?.length ? `notified: ${g.notifiedTo.join(", ")}` : "no notification sent"}
                      </div>
                    </TicketRow>
                  )} />
              )}
          </Card>

          {/* ── sentiment + coaching ── */}
          <Card section="sentiment" title="Customer sentiment & coaching"
            kicker={sent?.analysedAt ? `From this technician's ticket notes · analysed ${new Date(sent.analysedAt).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : "From this technician's ticket notes"}
            accent="var(--cal)"
            right={(
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {/* The count is the useful part while it builds: each client is
                    one AI call, so "3 of 12" tells you how much is left in a way
                    a spinner cannot. */}
                {/* ⛔ NO BUILD AFFORDANCE ON PAPER. Rendering is read-only:
                    the handout prints what is stored and never offers to
                    compute. A print tab that can start twelve Anthropic calls
                    five minutes before a 1:1 is the behaviour that produced the
                    230-second timeout. Build it on the page, then print. */}
                <button className={`btn${printing ? " tr-print-hide" : ""}`} style={{ fontSize: 10, padding: "5px 10px", display: printing ? "none" : undefined }}
                  onClick={analyseSentiment} disabled={analysing || !resourceId}>
                  {analysing
                    ? (sentimentProgress && sentimentProgress.total
                        ? `Analysing… ${sentimentProgress.done} of ${sentimentProgress.total}`
                        : "Analysing…")
                    : sent ? "↻ Re-analyse" : "Analyse sentiment"}
                </button>
                {sent?.avgScore != null && (
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: scoreColour(sent.avgScore), lineHeight: 1 }}>{num(sent.avgScore, 1)}</div>
                    <div style={{ fontSize: 8, color: "var(--text3)" }}>/10 avg</div>
                  </div>
                )}
              </div>
            )}>
            {sentimentError && (
              <div style={{ padding: "8px 14px", fontSize: 10.5, color: "var(--danger)" }}>{sentimentError}</div>
            )}
            {!sent ? (
              <Empty>
                {printing ? (
                  <>Sentiment not analysed for this period.<br />
                  <span style={{ fontSize: 9 }}>Build it on the Team Reporting page before printing — this sheet
                  renders what is stored and never starts an analysis of its own.</span></>
                ) : analysing ? (
                  <>Reading this technician’s ticket notes and analysing them client by client…<br />
                  {sentimentProgress && sentimentProgress.total
                    ? <>Done <strong>{sentimentProgress.done}</strong> of <strong>{sentimentProgress.total}</strong> clients — results appear here as each one lands.</>
                    : <>This takes a moment — it is one AI call per client they touched.</>}</>
                ) : (
                  <>No sentiment build for this technician and period yet.<br />
                  Press <strong>Analyse sentiment</strong> to run it — it reads the same ticket notes the Autotask page
                  analyses. It is kept separate from <strong>Rebuild from source</strong> so refreshing the numbers
                  never re-spends the AI call.</>
                )}
              </Empty>
            ) : (
              <>
                {/* ⚠ A PARTIAL SECTION MUST SAY WHAT IS ABSENT, BY NAME. Printed
                    without this, nine analysed clients read as the whole set —
                    the same failure as rendering a missing source as 0, and
                    dearer, because the analyses that DID run were paid for. On
                    paper there is no "still building" context to infer it from,
                    so the sheet has to carry it. */}
                {sent.partial && sent.missingClients?.length > 0 && (
                  <div className="tr-block" style={{ margin: "8px 14px 0", padding: "8px 11px", borderRadius: 6, background: "var(--warn-d)", border: "1px solid rgba(230,126,34,0.28)", fontSize: 9.5, color: "var(--text2)", lineHeight: 1.5, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
                    <strong>{sent.done} of {sent.total} clients analysed.</strong>{" "}
                    Not yet built: {sent.missingClients.map((c) => c.companyName).join(", ")}.
                    {printing
                      ? " Build the remainder on the Team Reporting page and reprint."
                      : " Press Re-analyse to finish the remainder."}
                  </div>
                )}
                {sent.byClient?.length > 0 && (
                  <>
                    <div style={{ padding: "6px 14px 5px", fontSize: 8.5, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text3)", background: "var(--bg)" }}>By client — tap a row for the reasoning</div>
                    {sent.byClient.map((c) => <SentimentClientCard key={c.companyId} c={c} />)}
                  </>
                )}
                {sent.coaching && (
                  <div style={{ borderTop: "2px solid var(--border)", padding: "14px 16px", background: "linear-gradient(180deg,#fffdf7,white)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--cal)", marginBottom: 12 }}>🎓 Coaching notes for the 1:1</div>
                    <CoachingBlock heading="What they did well" icon="✅" colour={GREEN} bg="rgba(39,174,96,0.06)" items={sent.coaching.positives} />
                    <CoachingBlock heading="What pulled the score down" icon="⚠️" colour={RED} bg="rgba(231,76,60,0.06)" items={sent.coaching.whatWentWrong} />
                    <CoachingBlock heading="Key moments from the interactions" icon="💬" colour="var(--text2)" bg="var(--bg)" items={sent.coaching.keyMoments} />
                    <CoachingBlock heading="Coaching tips" icon="💡" colour="var(--strat)" bg="rgba(11,128,165,0.05)" items={sent.coaching.coachingTips} numbered />
                    <div style={{ fontSize: 9, color: "var(--text3)", marginTop: 4 }}>Generated from ticket notes · for internal coaching use only</div>
                  </div>
                )}
              </>
            )}
          </Card>

          {/* ── IT Glue ── */}
          <Card section="it-glue" title="IT Glue — documenting as they work"
            kicker={glue?.matched === false ? "This technician has no IT Glue identity mapped yet." : `Items created or updated in the period${glue?.organisations ? ` across ${glue.organisations} client${glue.organisations !== 1 ? "s" : ""}` : ""}`}
            accent="var(--fellow)">
            {glue?.matched === false ? (
              <Empty>
                No IT Glue user matched to <strong>{tech?.name}</strong>.<br />
                Map their IT Glue user id in the Team Reporting config so documentation activity can be attributed.
              </Empty>
            ) : !glue ? <Empty>IT Glue activity log unavailable for this period.</Empty> : (
              <>
                {/* A truncated audit trail reads exactly like a quiet week — say so, loudly,
                    rather than letting a partial count pass as a fact. */}
                {glue.incompleteDays?.length > 0 && (
                  <div style={{ padding: "8px 14px", fontSize: 10, color: "var(--warn)", background: "var(--warn-d)", borderBottom: "1px solid var(--border)", lineHeight: 1.5 }}>
                    ⚠ Activity log incomplete for {glue.incompleteDays.length} day{glue.incompleteDays.length !== 1 ? "s" : ""} ({glue.incompleteDays.map((d) => fmtDay(d)).join(", ")}).
                    IT Glue truncated those pages, so the counts below are a floor, not a total — don't read a low number as a quiet week.
                  </div>
                )}
                <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--border)" }}>
                  {[{ l: "Created", v: glue.created, c: GREEN }, { l: "Updated", v: glue.updated, c: "var(--strat)" }, { l: "Clients touched", v: glue.organisations, c: "var(--text2)" }].map((x) => (
                    <div key={x.l} style={{ flex: 1, padding: "10px 14px", borderRight: "1px solid var(--border)", textAlign: "center" }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: x.c, lineHeight: 1 }}>{x.v ?? "—"}</div>
                      <div style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text3)", marginTop: 4 }}>{x.l}</div>
                    </div>
                  ))}
                </div>
                {glue.byType?.length > 0 && (
                  <div style={{ padding: "8px 14px", display: "flex", flexWrap: "wrap", gap: 5, borderBottom: glue.recent?.length ? "1px solid var(--border)" : "none" }}>
                    {glue.byType.map((b) => (
                      <span key={b.resourceType} style={{ fontSize: 9, padding: "2px 7px", borderRadius: 4, background: "var(--s2)", border: "1px solid var(--border)", color: "var(--text2)" }}>
                        {b.resourceType}: <strong>{(b.created || 0) + (b.updated || 0)}</strong>
                      </span>
                    ))}
                  </div>
                )}
                {glue.recent?.length > 0 && (
                  <Expandable printing={printing} items={glue.recent} initial={5} moreLabel="entries"
                    render={(r, i) => (
                      <div key={i} style={{ display: "flex", gap: 10, padding: "6px 14px", borderTop: i === 0 ? "none" : "1px solid var(--border)", alignItems: "center" }}>
                        <span style={{ fontSize: 9, width: 92, flexShrink: 0, color: "var(--text3)" }}>{new Date(r.at).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                        <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", width: 58, flexShrink: 0, color: r.action === "create" ? GREEN : "var(--strat)" }}>{r.action}</span>
                        <span style={{ fontSize: 10, color: "var(--text2)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.resourceType} · {r.resourceName}</span>
                        <span style={{ fontSize: 9, color: "var(--text3)", flexShrink: 0, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.organizationName}</span>
                      </div>
                    )} />
                )}
              </>
            )}
          </Card>

          {/* ── quality / context ── */}
          <Card section="quality" title="Quality & workload context" kicker="Read the numbers above against this — a tech on hard L3 tickets is legitimately slower." accent="var(--warn)">
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {[
                { l: "Avg resolution", v: thr?.avgResolutionHours != null ? `${num(thr.avgResolutionHours, 1)}h` : "—", s: "created → completed, business hrs" },
                { l: "Reopened", v: thr?.reopened ?? "—", s: "completed by them, later reopened" },
                { l: "Open / ageing", v: thr?.openAgeing ?? "—", s: "assigned, no update in 5 business days" },
                { l: "Notes written", v: thr?.notes ?? "—", s: "excludes system & workflow notes" },
                { l: "Time entries", v: ts?.entries ?? "—", s: "billable capture" },
              ].map((x) => (
                <div key={x.l} style={{ flex: "1 1 160px", padding: "11px 14px", borderRight: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: "var(--text)", lineHeight: 1 }}>{x.v}</div>
                  <div style={{ fontSize: 9.5, fontWeight: 600, color: "var(--text2)", marginTop: 4 }}>{x.l}</div>
                  <div style={{ fontSize: 8.5, color: "var(--text3)", marginTop: 1 }}>{x.s}</div>
                </div>
              ))}
            </div>
          </Card>

          <div style={{ fontSize: 9.5, color: "var(--text3)", lineHeight: 1.6, padding: "0 2px" }}>
            Balanced by design — responsiveness, throughput, quality and documentation, with no single blended score.
            Present every number with its context in the 1:1, and use the drill-downs to talk about specific tickets rather than aggregates.
          </div>
        </>
      )}
    </div>
  );
}
